import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import type { Message } from '@/pages/chat/types/chat';
import { welcomeSpreadService } from '@/services/WelcomeSpreadService';
import { useTranslation } from 'react-i18next';

interface WelcomeSpreadContextType {
    messages: Message[];
    isTyping: boolean;
    isLoading: boolean;
    error: string | null;
    sessionId: string | null;
    addMessage: (message: Message) => void;
    sendMessage: (content: string) => Promise<void>;
    clearMessages: () => void;
    initializeSession: () => Promise<void>;
}

const WelcomeSpreadContext = createContext<WelcomeSpreadContextType | undefined>(undefined);

export function WelcomeSpreadProvider({ children }: { children: ReactNode }) {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<Message[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Initialize with welcome message
    useEffect(() => {
        if (!isInitialized) {
            const initialMessage: Message = {
                id: "welcome-initial",
                content: t('welcomeSpread.initialMessage'),
                role: "assistant" as const,
                timestamp: new Date(),
            };
            setMessages([initialMessage]);
            setIsInitialized(true);
        }
    }, [t, isInitialized]);

    const addMessage = useCallback((message: Message) => {
        setMessages(prev => [...prev, message]);
    }, []);

    const initializeSession = useCallback(async () => {
        if (sessionId) return; // Already have a session

        setIsLoading(true);
        try {
            const newSessionId = await welcomeSpreadService.createSession();
            if (newSessionId) {
                setSessionId(newSessionId);
            } else {
                setError(welcomeSpreadService.getError());
            }
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setIsLoading(false);
        }
    }, [sessionId]);

    const sendMessage = useCallback(async (content: string) => {
        if (!content.trim()) return;

        // Add user message
        const userMessage: Message = {
            id: `user-${Date.now()}`,
            content,
            role: "user" as const,
            timestamp: new Date(),
        };
        addMessage(userMessage);

        // Show typing indicator
        setIsTyping(true);
        setIsLoading(true);
        setError(null);

        try {
            // Check if this is the first user message (only initial assistant message exists)
            const userMessagesCount = messages.filter(msg => msg.role === 'user').length;
            const isFirstMessage = userMessagesCount === 0;
            const step = isFirstMessage ? 1 : undefined;

            // Send message to API
            const response = await welcomeSpreadService.sendMessage(content, step);

            if (response) {
                // Update session ID if it was created
                if (response.sessionId && !sessionId) {
                    setSessionId(response.sessionId);
                }

                // If this is step 1, save the entire response payload to localStorage
                if (response.step === 1) {
                    const step1Payload = {
                        message: response.message,
                        sessionId: response.sessionId,
                        timestamp: response.timestamp,
                        spreadId: response.spreadId,
                        step: response.step,
                        userQuestion: content
                    };
                    localStorage.setItem('welcomeSpread_step1', JSON.stringify(step1Payload));
                }

                // Simulate a delay for better UX
                await new Promise(resolve => setTimeout(resolve, 1000));

                // Add AI response
                const aiMessage: Message = {
                    id: `assistant-${Date.now()}`,
                    content: response.message || t('welcomeSpread.thankYouMessage'),
                    role: "assistant" as const,
                    timestamp: new Date(response.timestamp),
                    ...(response.spreadId && { spreadId: response.spreadId }),
                    ...(response.step && { step: response.step }),
                };
                addMessage(aiMessage);
            } else {
                // Fallback response if API fails
                const fallbackMessage: Message = {
                    id: `assistant-${Date.now()}`,
                    content: t('welcomeSpread.thankYouMessage'),
                    role: "assistant" as const,
                    timestamp: new Date(),
                };
                addMessage(fallbackMessage);
                setError(welcomeSpreadService.getError());
            }
        } catch (err) {
            setError((err as Error).message);
            // Still add a fallback message on error
            const fallbackMessage: Message = {
                id: `assistant-${Date.now()}`,
                content: t('welcomeSpread.thankYouMessage'),
                role: "assistant" as const,
                timestamp: new Date(),
            };
            addMessage(fallbackMessage);
        } finally {
            setIsTyping(false);
            setIsLoading(false);
        }
    }, [addMessage, messages, sessionId, t]);

    const clearMessages = useCallback(() => {
        setMessages([]);
        setSessionId(null);
        setError(null);
        welcomeSpreadService.clearSession();
        setIsInitialized(false);
    }, []);

    return (
        <WelcomeSpreadContext.Provider
            value={{
                messages,
                isTyping,
                isLoading,
                error,
                sessionId,
                addMessage,
                sendMessage,
                clearMessages,
                initializeSession,
            }}
        >
            {children}
        </WelcomeSpreadContext.Provider>
    );
}

export function useWelcomeSpreadContext() {
    const context = useContext(WelcomeSpreadContext);
    if (context === undefined) {
        throw new Error('useWelcomeSpreadContext must be used within a WelcomeSpreadProvider');
    }
    return context;
}
